/**
 * AppShell — layout principal (sidebar + header + content).
 * Mobile usa bottom nav; desktop usa sidebar fixa.
 */

import { type ReactNode, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ShiftHandoverButton from '@/features/shift-handover/ShiftHandoverButton';
import SemPlantaoBanner from '@/features/shift-handover/SemPlantaoBanner';
import Header from './Header/Header';
import { AvisoNovaVersaoConectado } from './AvisoNovaVersao';
import { RelogioDesreguladoBannerConectado } from './RelogioDesreguladoBanner';

// Ordem/ícones/labels espelham o protótipo V5 (MODULES_A + MODULES_B), restritos
// aos módulos já presentes no app (Sirenes/Câmeras/IA do protótipo ficam fora).
const MODULES = [
  { id: 'M1',  path: '/dashboard',      label: 'Dashboard',       icon: '🏠', status: 'funcional' as const,    disabled: false },
  { id: 'M2',  path: '/mapa',           label: 'Mapa',            icon: '🗺️', status: 'placeholder' as const, disabled: false },
  { id: 'M3',  path: '/tarp',           label: 'TARP',            icon: '🛡️', status: 'funcional' as const,    disabled: false },
  { id: 'MPA', path: '/planos',         label: 'Planos de Ação',  icon: '🗂️', status: 'funcional' as const,    disabled: false },
  { id: 'MPAEBM', path: '/paebm',       label: 'PAEBM',           icon: '🆘', status: 'funcional' as const,    disabled: false },
  { id: 'M10', path: '/log',            label: 'Log',             icon: '📜', status: 'funcional' as const,    disabled: false },
  { id: 'M6',  path: '/instrumentacao', label: 'Instrumentação',  icon: '📈', status: 'funcional' as const,    disabled: false },
  { id: 'M7',  path: '/fs-quinzenal',   label: 'FS Quinzenal',    icon: '🔢', status: 'placeholder' as const, disabled: true },
  { id: 'M9',  path: '/conformidade',   label: 'Conformidade',    icon: '📋', status: 'placeholder' as const, disabled: true },
  { id: 'M12', path: '/historico',      label: 'Hist. Inspeção',  icon: '📅', status: 'placeholder' as const, disabled: true },
  { id: 'MOUT', path: '/outros',        label: 'Outros',          icon: '📚', status: 'funcional' as const,    disabled: false },
] as const;

// Badge de perfil no topo da sidebar (paridade com o protótipo V5): verde pra
// operador (perfil A), amarelo pra técnico responsável/admin (perfil B).
const PROFILE_BADGE: Record<string, { title: string; sub: string; tone: 'a' | 'b' }> = {
  operador:            { title: '👷 Operador',            sub: 'Leitura e plantão', tone: 'a' },
  tecnico_responsavel: { title: '🎓 Técnico Responsável', sub: 'Acesso completo',   tone: 'b' },
  admin:               { title: '⚙️ Administrador',       sub: 'Administração',     tone: 'b' },
};

// Item de menu (paridade com `.nav-btn` do protótipo V5): recuado (mx-2),
// arredondado (rounded-lg) e com acento amarelo à esquerda (border-l 3px) — que,
// por causa do cantos arredondados, fica com as pontas curvas (não uma listra reta).
const NAV_ITEM_BASE =
  'flex items-center gap-2.5 mx-2 my-0.5 px-2.5 py-2 rounded-lg border-l-[3px] text-[13px] font-medium transition';

function navItemClass(isActive: boolean): string {
  return `${NAV_ITEM_BASE} ${
    isActive
      ? 'border-jf-yellow bg-jf-border text-jf-yellow'
      : 'border-transparent text-jf-muted hover:bg-white/[0.04] hover:text-white'
  }`;
}

export default function AppShell({ children }: { children?: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { profile } = useAuth();
  const badge = profile ? PROFILE_BADGE[profile] : undefined;

  return (
    <div className="flex flex-col h-dvh bg-jf-bg text-white overflow-hidden">
      {/* ── Header (GUPM-158: chips A/B/C + tilt + contatos) ── */}
      <Header onToggleSidebar={() => setSidebarOpen((s) => !s)} />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar (desktop) ────────────────────────────── */}
        <nav
          className={`hidden md:flex flex-col bg-jf-surface2 border-r border-jf-border transition-all overflow-y-auto ${
            sidebarOpen ? 'w-56' : 'w-0'
          }`}
        >
          {badge && (
            <div
              className={`mx-2 mt-2.5 mb-1.5 px-2.5 py-2 rounded-lg border ${
                badge.tone === 'a'
                  ? 'bg-level-normal/10 border-level-normal/30'
                  : 'bg-jf-yellow/[0.08] border-jf-yellow/25'
              }`}
            >
              <div className={`text-[11px] font-bold ${badge.tone === 'a' ? 'text-level-normal' : 'text-jf-yellow'}`}>
                {badge.title}
              </div>
              <div className="text-[10px] text-jf-dim mt-0.5">{badge.sub}</div>
            </div>
          )}

          <ShiftHandoverButton />

          {MODULES.map((m) => (
            m.disabled ? (
              <div
                key={m.id}
                aria-disabled="true"
                title="Módulo indisponível"
                className={`${NAV_ITEM_BASE} border-transparent text-jf-dim opacity-50 cursor-not-allowed select-none`}
              >
                <span className="text-base" aria-hidden>{m.icon}</span>
                <span className="flex-1 truncate">{m.label}</span>
                <span className="text-[9px]" title="Indisponível">🔒</span>
              </div>
            ) : (
              <NavLink
                key={m.id}
                to={m.path}
                className={({ isActive }) => navItemClass(isActive)}
              >
                <span className="text-base" aria-hidden>{m.icon}</span>
                <span className="flex-1 truncate">{m.label}</span>
                {m.status === 'placeholder' && (
                  <span className="text-[9px] text-orange-400" title="Placeholder — aguardando integração">●</span>
                )}
              </NavLink>
            )
          ))}

          {/* Auditoria — visível apenas pra engenheiro/admin */}
          {(profile === 'admin' || profile === 'tecnico_responsavel') && (
            <>
              <div className="mt-3 p-2 text-xs uppercase tracking-wide text-jf-dim border-t border-jf-border">
                Auditoria
              </div>
              <NavLink
                to="/auditoria"
                className={({ isActive }) => navItemClass(isActive)}
              >
                <span className="text-base" aria-hidden>🔎</span>
                <span className="flex-1 truncate">Auditoria</span>
              </NavLink>
              <NavLink
                to="/historico-trocas"
                className={({ isActive }) => navItemClass(isActive)}
              >
                <span className="text-base" aria-hidden>🔁</span>
                <span className="flex-1 truncate">Trocas de Plantão</span>
              </NavLink>
            </>
          )}

          <div className="mt-auto p-3 text-[10px] text-jf-dim border-t border-jf-border">
            <p>🟢 funcional</p>
            <p>🟠 aguardando integração</p>
          </div>
        </nav>

        {/* ── Conteúdo ─────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">
          {/* Ausência de plantonista vale para QUALQUER tela e QUALQUER perfil —
              por isso mora aqui, e não na sidebar (que é `hidden` no mobile,
              justamente o aparelho de quem está em campo). GUPM-273. */}
          <SemPlantaoBanner />
          <RelogioDesreguladoBannerConectado />
          {children ?? <Outlet />}
        </main>
      </div>

      {/* ── Bottom nav (mobile) ─────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 flex bg-jf-surface2 border-t border-jf-border z-50 overflow-x-auto">
        {MODULES.map((m) => (
          m.disabled ? (
            <div
              key={m.id}
              aria-disabled="true"
              title="Módulo indisponível"
              className="flex-shrink-0 flex flex-col items-center px-3 py-2 text-[10px] text-jf-dim opacity-50 cursor-not-allowed select-none"
            >
              <span className="text-base" aria-hidden>{m.icon}</span>
              {m.label}
            </div>
          ) : (
            <NavLink
              key={m.id}
              to={m.path}
              className={({ isActive }) =>
                `flex-shrink-0 flex flex-col items-center px-3 py-2 text-[10px] ${
                  isActive ? 'text-jf-yellow' : 'text-jf-muted'
                }`
              }
            >
              <span className="text-base" aria-hidden>{m.icon}</span>
              {m.label}
            </NavLink>
          )
        ))}
      </nav>

      {/* GUPM-302: aviso de nova versão. Montado no shell (não por rota) para ficar
          disponível em QUALQUER tela — é fixo no canto inferior esquerdo e não rola
          com a página. Vem depois da nav no DOM, mas o posicionamento é por
          `position: fixed` + `z-index`, não por ordem. */}
      <AvisoNovaVersaoConectado />
    </div>
  );
}
